import { useState, useEffect, useRef } from "react";
import { IoMdCheckmark } from "react-icons/io";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch } from "react-redux";
import styles from "../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { toast } from "../toast";
import {
  createLabService,
  updateLabService,
} from "../../services/labTestsService";
import { APP_PATH } from "../../api/backendPath";
import axios from "axios";

// Service code format: uppercase letters/digits in hyphen-separated groups
// (e.g. LAB-10234). No leading/trailing/double hyphens, spaces or symbols.
const SERVICE_CODE_REGEX = /^[A-Z0-9]+(-[A-Z0-9]+)*$/;
const SERVICE_CODE_MIN = 3;
const SERVICE_CODE_MAX = 20;

const validateServiceCode = (code) => {
  const value = (code || "").trim();
  if (!value) return "Service code is required";
  if (value.length < SERVICE_CODE_MIN || value.length > SERVICE_CODE_MAX) {
    return `Service code must be ${SERVICE_CODE_MIN}-${SERVICE_CODE_MAX} characters`;
  }
  if (!SERVICE_CODE_REGEX.test(value)) {
    return "Use uppercase letters, numbers and hyphens only (e.g. LAB-10234)";
  }
  return "";
};

// Center prefix = first 3 letters of the center name, uppercased
// (e.g. "Sofia Lab" -> "SOF"). Falls back to "LAB" when unavailable.
const deriveServicePrefix = (centerName) =>
  (centerName || "")
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 3)
    .toUpperCase();

// Status is currently hidden from the Add/Edit Test dialog; services always
// default to ACTIVE. Flip this to true to bring the Status toggle back.
const SHOW_STATUS_FIELD = false;

export default function LabDialogAddEditTest({
  open,
  onClose,
  children,
  selectedAppointment,
  selectedTest,
  type = "add",
  centre = "lab",
  onSuccess,
}) {
  const [serviceCode, setServiceCode] = useState("");
  const [serviceCodeError, setServiceCodeError] = useState("");
  // 3-letter center prefix (e.g. "SOF"); the "SOF-" segment is locked/uneditable.
  const [servicePrefix, setServicePrefix] = useState("");
  // Tracks whether the user has manually edited the code, so the async
  // auto-generation doesn't overwrite their input when it resolves.
  const manuallyEditedRef = useRef(false);
  const [testName, setTestName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [testNameError, setTestNameError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [priceError, setPriceError] = useState("");
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
      resetForm();
    }
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  // Fetch categories when modal opens
  useEffect(() => {
    if (open) {
      setCategoriesLoading(true);
      const token = sessionStorage.getItem("usr_1a2b3c");
      axios
        .get(
          `${process.env.REACT_APP_API_URL}/api/v1/lab/services/categories`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .then((res) => setCategories(res.data?.data || []))
        .catch(() => setCategories([]))
        .finally(() => setCategoriesLoading(false));
    }
  }, [open]);

  // On open: derive the center prefix from the profile (used to lock the input
  // prefix), and on ADD fetch the next SEQUENTIAL service code from the backend
  // (e.g. "SOF-0001"). Uniqueness is guaranteed per center by the backend.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const init = async () => {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const headers = { Authorization: `Bearer ${token}` };

      // 1) Center prefix from the profile.
      let prefix = "";
      try {
        const profileRes = await axios.get(
          `${process.env.REACT_APP_API_URL}${APP_PATH.labProfile}`,
          { headers },
        );
        prefix = deriveServicePrefix(
          profileRes?.data?.data?.lab_name_english || "",
        );
      } catch (err) {
        console.warn("Failed to fetch lab profile for service prefix:", err);
      }
      if (cancelled) return;
      setServicePrefix(prefix);

      // 2) On ADD, ask the backend for the next sequential code for this center.
      if (type === "add") {
        try {
          const url = `${process.env.REACT_APP_API_URL}${APP_PATH.labServices}/code/generate${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ""}`;
          const codeRes = await axios.get(url, { headers });
          const nextCode = codeRes?.data?.data?.serviceCode || "";
          if (!cancelled && nextCode && !manuallyEditedRef.current) {
            setServiceCode(nextCode);
          }
        } catch (err) {
          console.warn("Failed to generate next lab service code:", err);
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [open, type]);

  // Populate form when editing
  useEffect(() => {
    if (open && type === "edit" && selectedTest) {
      setServiceCode(
        selectedTest.serviceCode || selectedTest.service_code || "",
      );
      setServiceCodeError("");
      setTestName(selectedTest.testName || selectedTest.serviceName || "");
      setCategoryId(
        selectedTest.serviceCategoryId?.toString() ||
          selectedTest.categoryId?.toString() ||
          "",
      );
      setPrice(selectedTest.price?.toString() || "");
      setStatus(
        selectedTest.status === "Active" || selectedTest.status === "ACTIVE",
      );
    } else if (open && type === "add") {
      // Start empty; the async effect fills the next sequential code once it
      // resolves (unless the user starts typing first).
      manuallyEditedRef.current = false;
    }
  }, [open, type, selectedTest]);

  const resetForm = () => {
    setServiceCode("");
    setServiceCodeError("");
    setServicePrefix("");
    manuallyEditedRef.current = false;
    setTestName("");
    setCategoryId("");
    setPrice("");
    setStatus(true);
    setLoading(false);
    setTestNameError("");
    setCategoryError("");
    setPriceError("");
  };

  const handleSave = async () => {
    // Validate all mandatory fields and surface inline errors. Collect every
    // error so all missing fields show at once, then block the submit.
    let hasError = false;

    const codeError = validateServiceCode(serviceCode);
    if (codeError) {
      setServiceCodeError(codeError);
      hasError = true;
    }

    if (!testName.trim()) {
      setTestNameError("Test name is required");
      hasError = true;
    }
    if (!categoryId) {
      setCategoryError("Category is required");
      hasError = true;
    }
    const parsedPrice = price
      ? parseFloat(String(price).replace(/,/g, ""))
      : NaN;
    if (!String(price).trim()) {
      setPriceError("Price is required");
      hasError = true;
    } else if (isNaN(parsedPrice) || parsedPrice <= 0) {
      setPriceError("Enter a valid price");
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);
    try {
      const testData = {
        serviceCode: serviceCode.trim(),
        testName: testName.trim(),
        serviceName: testName.trim(),
        serviceCategoryId: categoryId ? parseInt(categoryId) : undefined,
        price: parsedPrice,
        closingTime: "20:00",
        // Status is hidden from the UI and always defaults to ACTIVE on add/edit.
        // If the field is re-enabled (SHOW_STATUS_FIELD), the toggle drives it.
        status: SHOW_STATUS_FIELD ? (status ? "ACTIVE" : "INACTIVE") : "ACTIVE",
        // Location field removed from UI; persist as null in the database
        location: null,
      };

      if (type === "add") {
        await createLabService(testData);
        toast.success("Test added successfully");
      } else {
        await updateLabService(selectedTest.id, testData);
        toast.success("Test updated successfully");
      }

      if (onSuccess) onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to save test";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // The locked, non-editable prefix segment (e.g. "SOF-"). On ADD it's always
  // the center prefix; on EDIT it's only locked when the existing code already
  // uses that prefix (so legacy codes with a different prefix aren't mangled).
  const lockedPrefix = servicePrefix
    ? type === "add" || serviceCode.startsWith(`${servicePrefix}-`)
      ? `${servicePrefix}-`
      : ""
    : "";

  const handleServiceCodeChange = (e) => {
    // Restrict to uppercase letters, numbers and hyphens.
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    let next = raw;
    if (lockedPrefix) {
      // Keep everything after the locked prefix as the editable suffix; if the
      // user edited into/deleted the prefix, snap it back and preserve their suffix.
      let suffix = raw.startsWith(lockedPrefix)
        ? raw.slice(lockedPrefix.length)
        : raw.replace(new RegExp(`^${servicePrefix}?-*`), "");
      suffix = suffix.replace(/^-+/, ""); // no leading hyphen right after the prefix
      next = lockedPrefix + suffix;
    }
    manuallyEditedRef.current = true;
    setServiceCode(next);
    if (serviceCodeError) setServiceCodeError(validateServiceCode(next));
  };

  if (!open) return null;

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          <button className={styles["dialog-close"]} onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z"
                fill="currentcolor"
              />
            </svg>
          </button>

          <div className={styles["mb-24"]}>
            <h2 className={styles["dialog-title"]}>
              {type === "add" ? "Add New Test" : "Edit Test"}
            </h2>
          </div>

          <div className={styles["form-design"]}>
            {/* Service Code */}
            <div className={styles["form-group"]}>
              <label>
                Service Code
                <span
                  style={{ color: "#EF4444", marginLeft: 2 }}
                  aria-hidden="true"
                >
                  *
                </span>
              </label>
              <input
                type="text"
                placeholder={`e.g. ${servicePrefix || "LAB"}-0001`}
                value={serviceCode}
                maxLength={SERVICE_CODE_MAX}
                onChange={handleServiceCodeChange}
                onKeyDown={(e) => {
                  // Block Backspace/Delete when the caret is within the locked prefix.
                  if (!lockedPrefix) return;
                  const el = e.target;
                  const start = el.selectionStart ?? 0;
                  const end = el.selectionEnd ?? 0;
                  if (
                    (e.key === "Backspace" &&
                      start <= lockedPrefix.length &&
                      start === end) ||
                    (e.key === "Delete" && start < lockedPrefix.length) ||
                    (start < lockedPrefix.length &&
                      end <= lockedPrefix.length &&
                      (e.key === "Backspace" || e.key === "Delete"))
                  ) {
                    e.preventDefault();
                  }
                }}
                onBlur={() =>
                  setServiceCodeError(validateServiceCode(serviceCode))
                }
                className={styles["text-input"]}
              />
              {serviceCodeError && (
                <span className={styles["rule-invalid"]}>
                  {serviceCodeError}
                </span>
              )}
            </div>

            {/* Test Name */}
            <div className={styles["form-group"]}>
              <label>
                Test Name
                <span
                  style={{ color: "#EF4444", marginLeft: 2 }}
                  aria-hidden="true"
                >
                  *
                </span>
              </label>
              <input
                type="text"
                placeholder="Enter test name"
                value={testName}
                onChange={(e) => {
                  setTestName(e.target.value);
                  if (e.target.value.trim()) setTestNameError("");
                }}
                className={styles["text-input"]}
              />
              {testNameError && (
                <span className={styles["rule-invalid"]}>{testNameError}</span>
              )}
            </div>

            {/* Category */}
            <div className={styles["form-group"]}>
              <label>
                Category
                <span
                  style={{ color: "#EF4444", marginLeft: 2 }}
                  aria-hidden="true"
                >
                  *
                </span>
              </label>
              <select
                className={styles["select-input"]}
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  if (e.target.value) setCategoryError("");
                }}
                disabled={categoriesLoading}
              >
                <option value="">
                  {categoriesLoading ? "Loading..." : "Select category"}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id.toString()}>
                    {cat.name}
                  </option>
                ))}
              </select>
              {categoryError && (
                <span className={styles["rule-invalid"]}>{categoryError}</span>
              )}
            </div>

            {/* Price */}
            <div className={styles["form-group"]}>
              <label>
                Price (IQD)
                <span
                  style={{ color: "#EF4444", marginLeft: 2 }}
                  aria-hidden="true"
                >
                  *
                </span>
              </label>
              <input
                type="text"
                placeholder="eg. 10,000"
                value={price}
                onChange={(e) => {
                  setPrice(e.target.value);
                  if (e.target.value.trim()) setPriceError("");
                }}
                className={styles["text-input"]}
              />
              {priceError && (
                <span className={styles["rule-invalid"]}>{priceError}</span>
              )}
            </div>

            {/* Status (hidden — see SHOW_STATUS_FIELD; services default to ACTIVE) */}
            {SHOW_STATUS_FIELD && (
              <div className={styles["form-group"]}>
                <label>Status</label>
                <div className={styles["toggle-container"]}>
                  <span className={styles["toggle-label"]}>
                    {status ? "Active" : "Inactive"}
                  </span>
                  <label className={styles["toggle-switch"]}>
                    <input
                      type="checkbox"
                      checked={status}
                      onChange={(e) => setStatus(e.target.checked)}
                    />
                    <span className={styles["toggle-slider"]}></span>
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className={`${styles["dialog-footer"]}`}>
            <button
              className={`${styles["btn-outlined-1"]}`}
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className={styles["btn-1"]}
              onClick={handleSave}
              disabled={loading}
            >
              <IoMdCheckmark /> {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
